import { Organization } from "@/shared/services/organization.service";
import { TABLE_HEADERS } from "../organizations.constants";
import StatusBadge from "../../../shared/components/ui/StatusBadge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/table";
import { Button } from "@/shared/components/ui/button";
import { Edit, Power } from "lucide-react";
import { useState } from "react";
import { Select } from "@/shared/components/ui/select";

interface OrganizationsTableProps {
  organizations: Organization[];
  onEditOrganization: (organization: Organization) => void;
  onDeleteOrganization: (organization: Organization) => void;
  onActivateOrganization: (organization: Organization) => void;
}

const OrganizationsTable = ({
  organizations,
  onEditOrganization,
  onDeleteOrganization,
  onActivateOrganization,
}: OrganizationsTableProps) => {
  const [internalFilter, setInternalFilter] = useState<"all" | "internal" | "external">("all");

  const filteredOrganizations = organizations.filter((org) => {
    if (internalFilter === "all") return true;
    if (internalFilter === "internal") return org.is_internal;
    return !org.is_internal;
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Select
          value={internalFilter}
          onChange={(e) => setInternalFilter(e.target.value as "all" | "internal" | "external")}
          className="w-48"
        >
          <option value="all">All Organizations</option>
          <option value="internal">Internal Only</option>
          <option value="external">External Only</option>
        </Select>
      </div>

      <div className="overflow-auto max-h-[70vh] border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{TABLE_HEADERS.NAME}</TableHead>
              <TableHead>{TABLE_HEADERS.UID}</TableHead>
              <TableHead>{TABLE_HEADERS.STATUS}</TableHead>
              <TableHead>Internal</TableHead>
              <TableHead>{TABLE_HEADERS.USERS_COUNT}</TableHead>
              <TableHead>{TABLE_HEADERS.TARIFF}</TableHead>
              <TableHead>{TABLE_HEADERS.NEXT_PAYMENT_DATE}</TableHead>
              <TableHead>{TABLE_HEADERS.CREATED_AT}</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredOrganizations.map((org) => (
              <TableRow key={org.id}>
                <TableCell>{org.name}</TableCell>
                <TableCell>{org.uid}</TableCell>
                <TableCell>
                  <StatusBadge isActive={org.is_active} />
                </TableCell>
                <TableCell>
                  <StatusBadge isActive={org.is_internal} label={org.is_internal ? "Internal" : "External"} />
                </TableCell>
                <TableCell>{org.users_count}</TableCell>
                <TableCell>{org.tariff.name}</TableCell>
                <TableCell>{org.next_payment_date}</TableCell>
                <TableCell>{org.created_at}</TableCell>
                <TableCell>
                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onEditOrganization(org)}
                      className="p-2"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    {!org.is_active ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="p-2 text-green-500 hover:bg-green-500 hover:text-white"
                        onClick={() => onActivateOrganization(org)}
                        title="Activate organization"
                      >
                        <Power className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="p-2 text-red-500 hover:bg-red-500 hover:text-white"
                        onClick={() => onDeleteOrganization(org)}
                        title="Deactivate organization"
                      >
                        <Power className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default OrganizationsTable;
